import React, { useEffect, useRef, useState } from 'react';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  CircularProgress,
  useMediaQuery,
  Fade,
  Grid,
  Stack,
  Typography,
} from '@mui/material';

import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import { ExpandMore } from '@mui/icons-material';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

import {
  addCommas,
  convertPersianToEnglish,
} from '../../../components/converter/index';

import { getParcelScanKey } from './parcelScan';
import TaskBarcodeScan from './taskBarcodeScan';
import ApiClient from '../../../config/api';
import {
  normalizeTaskDetailItems,
  TASK_DETAIL_PAGE_SIZE,
} from './openMissionDetail';

const getDetailTotalCount = (task, loadedCount) => {
  const total = Number(
    task?.details?.total_count ??
      task?.details?.totalCount ??
      task?.count ??
      loadedCount
  );

  return Number.isFinite(total) ? total : loadedCount;
};

const getRowIdentity = (detail, parcel, index) =>
  String(detail?.id ?? parcel?.id ?? parcel?.barcode ?? index);

const getInlineStatuses = (...sources) =>
  sources
    .flatMap((source) => [
      source?.statusOptions,
      source?.statuses,
      source?.validStatuses,
      source?.validStatus,
      source?.availableStatuses,
      source?.cycleStatuses,
      source?.cycleStatus,
      source?.statusData,
    ])
    .find((statusList) => Array.isArray(statusList) && statusList.length) || [];

const hasOtpRequiredStatus = (statuses = []) =>
  statuses.some(
    (status) =>
      Boolean(status?.setOtpRequired) ||
      hasOtpRequiredStatus(status?.leafs || [])
  );

const DetailModal = ({
  openDetail,
  setOpenDetail,
  selectedData,
  setOpenGetParcel,
  setOpenChangeStatusConfirm,
  missionNo,
  setSelectedParcel,
  selectedTask,
  selectedTaskDetail,
}) => {
  const [expanded, setExpanded] = useState(-1);
  const [scannedKeys, setScannedKeys] = useState([]);
  const [parcelData, setParcelData] = useState([]);
  const [parcelDetails, setParcelDetails] = useState([]);
  const [detailPage, setDetailPage] = useState(1);
  const [detailTotalCount, setDetailTotalCount] = useState(0);
  const [hasMoreDetails, setHasMoreDetails] = useState(false);
  const [loadingMoreDetails, setLoadingMoreDetails] = useState(false);
  const parcelScrollerRef = useRef(null);
  const loadMoreSentinelRef = useRef(null);
  const loadMoreDetailsRef = useRef(null);
  const detailRequestRef = useRef(0);
  const loadingMoreRef = useRef(false);
  const scannedKeysRef = useRef([]);
  const apiRef = useRef(null);

  if (!apiRef.current) apiRef.current = ApiClient();

  const isSmallerThan290 = useMediaQuery('(max-width:290px)');

  const taskStatusOptions = getInlineStatuses(
    selectedTask,
    selectedTask?.details
  );

  const itemStatusOptions =
    parcelDetails.flatMap((item) =>
      getInlineStatuses(item, item?.parcel)
    ) || [];

  const showBulkChangeStatus = !hasOtpRequiredStatus([
    ...taskStatusOptions,
    ...itemStatusOptions,
  ]);

  const handleChangeStatusConfirm = (index) => {
    setSelectedParcel(parcelDetails[index]);
    setOpenChangeStatusConfirm('parcel');
  };

  const handleBarcode = (code) => {
    const scannedBarcode = convertPersianToEnglish(code).trim();

    const matchedItem = parcelData.find(
      (item) => scannedBarcode === String(item?.barcode ?? '').trim()
    );

    if (!matchedItem) {
      return;
    }

    const key = String(matchedItem?.barcode ?? '').trim();

    if (!key) {
      console.log('MATCHED ITEM HAS NO BARCODE');

      return;
    }

    setScannedKeys((prev) => {
      if (prev.includes(key)) {
        console.log('BARCODE ALREADY SCANNED:', key);

        return prev;
      }

      const next = [...prev, key];
      scannedKeysRef.current = next;

      console.log('UPDATED SCANNED KEYS:', next);

      return next;
    });
  };

  const parcelRows = parcelData.map((item, index) => ({
    item,
    index,
    key: getParcelScanKey(item, parcelDetails[index], index),
  }));

  const scannedParcels = parcelRows.filter(({ item }) =>
    scannedKeys.includes(String(item?.barcode ?? '').trim())
  );

  const unscannedParcels = parcelRows.filter(
    ({ item }) => !scannedKeys.includes(String(item?.barcode ?? '').trim())
  );

  const getPendingActivities = (rows) =>
    rows
      .map(({ index }) => parcelDetails[index])
      .filter((activity) => activity?.id != null && !activity.hasStatus);

  const handleGroupChangeStatus = (rows, label) => {
    const activities = getPendingActivities(rows);
    if (!activities.length) return;

    setSelectedParcel(null);
    setOpenChangeStatusConfirm({ type: 'group', activities, label });
  };

  useEffect(() => {
    if (openDetail) {
      const initialParcels = selectedData || [];
      const initialDetails = selectedTaskDetail || [];
      const totalCount = getDetailTotalCount(
        selectedTask,
        initialParcels.length
      );
      const detailMeta = selectedTask?.details || {};
      const rawTotalCount =
        detailMeta?.total_count ?? detailMeta?.totalCount ?? selectedTask?.count;
      const rawTotalPages = detailMeta?.total_pages ?? detailMeta?.totalPages;
      const rawHasNext = detailMeta?.has_next_page ?? detailMeta?.hasNextPage;

      setScannedKeys([]);
      scannedKeysRef.current = [];
      setExpanded(-1);
      setParcelData(initialParcels);
      setParcelDetails(initialDetails);
      setDetailPage(1);
      setDetailTotalCount(totalCount);
      setHasMoreDetails(
        initialParcels.length < totalCount ||
          Number(rawTotalPages) > 1 ||
          Boolean(rawHasNext) ||
          (rawTotalCount == null &&
            rawTotalPages == null &&
            rawHasNext == null &&
            initialParcels.length >= TASK_DETAIL_PAGE_SIZE)
      );
      setLoadingMoreDetails(false);
      loadingMoreRef.current = false;
      detailRequestRef.current += 1;

      window.requestAnimationFrame(() => {
        if (parcelScrollerRef.current) {
          parcelScrollerRef.current.scrollTop = 0;
        }
      });
    } else {
      detailRequestRef.current += 1;
    }
  }, [openDetail, selectedData, selectedTask, selectedTaskDetail]);

  useEffect(() => {
    scannedKeysRef.current = scannedKeys;
  }, [scannedKeys]);

  const loadMoreDetails = async () => {
    if (
      !openDetail ||
      !hasMoreDetails ||
      loadingMoreRef.current ||
      selectedTask?.id == null
    ) {
      return;
    }

    const nextPage = detailPage + 1;
    const requestId = ++detailRequestRef.current;
    loadingMoreRef.current = true;
    setLoadingMoreDetails(true);

    try {
      const response = await apiRef.current.get(
        '/api/app/v1/shipito/tasks/details',
        {
          params: {
            id: selectedTask.id,
            pageId: nextPage,
            pageSize: TASK_DETAIL_PAGE_SIZE,
          },
        }
      );

      if (requestId !== detailRequestRef.current || !openDetail) return;

      const nextData = response?.data?.data || {};
      const { details, parcels } = normalizeTaskDetailItems(
        nextData?.items || []
      );
      const existingKeys = new Set(
        parcelData.map((parcel, index) =>
          getRowIdentity(parcelDetails[index], parcel, index)
        )
      );
      const appendedDetails = [];
      const appendedParcels = [];

      parcels.forEach((parcel, index) => {
        const identity = getRowIdentity(details[index], parcel, index);
        if (existingKeys.has(identity)) return;
        existingKeys.add(identity);
        appendedDetails.push(details[index]);
        appendedParcels.push(parcel);
      });

      const totalCount = Number(
        nextData?.total_count ??
          nextData?.totalCount ??
          response?.data?.total_count ??
          response?.data?.totalCount ??
          detailTotalCount
      );
      const loadedCount = parcelData.length + appendedParcels.length;
      const meta = { ...(response?.data || {}), ...nextData };
      const reachedEnd =
        appendedParcels.length === 0 ||
        (Number.isFinite(totalCount) && loadedCount >= totalCount);

      setParcelData((previous) => [...previous, ...appendedParcels]);
      setParcelDetails((previous) => [...previous, ...appendedDetails]);
      setDetailPage(nextPage);
      setDetailTotalCount(totalCount);
      setHasMoreDetails(
        !reachedEnd &&
          (meta?.total_pages != null || meta?.totalPages != null
            ? nextPage < Number(meta?.total_pages ?? meta?.totalPages)
            : meta?.has_next_page != null || meta?.hasNextPage != null
            ? Boolean(meta?.has_next_page ?? meta?.hasNextPage)
            : appendedParcels.length >= TASK_DETAIL_PAGE_SIZE)
      );
    } catch (error) {
      console.error('Error fetching task details:', error);
    } finally {
      if (requestId === detailRequestRef.current) {
        loadingMoreRef.current = false;
        setLoadingMoreDetails(false);
      }
    }
  };

  loadMoreDetailsRef.current = loadMoreDetails;

  useEffect(() => {
    const root = parcelScrollerRef.current;
    const sentinel = loadMoreSentinelRef.current;

    if (!openDetail || !hasMoreDetails || !root || !sentinel) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      let animationFrame = null;
      const checkPosition = () => {
        if (animationFrame != null) return;
        animationFrame = window.requestAnimationFrame(() => {
          animationFrame = null;
          const remaining =
            root.scrollHeight - root.scrollTop - root.clientHeight;
          if (remaining < 480) loadMoreDetailsRef.current?.();
        });
      };

      checkPosition();
      root.addEventListener('scroll', checkPosition, { passive: true });
      return () => {
        root.removeEventListener('scroll', checkPosition);
        if (animationFrame != null) window.cancelAnimationFrame(animationFrame);
      };
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) loadMoreDetailsRef.current?.();
      },
      {
        root,
        rootMargin: '0px 0px 480px 0px',
        threshold: 0,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [openDetail, hasMoreDetails, loadingMoreDetails, parcelData.length]);

  return (
    <React.Fragment>
      <Dialog
        onClose={() => setOpenDetail(false)}
        aria-labelledby="customized-dialog-title"
        open={openDetail}
        maxWidth="sm"
        fullWidth={true}
        fullScreen={true}
        PaperProps={{
          sx: {
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 3,
            flexShrink: 0,
            bgcolor: 'background.paper',
            zIndex: 3,
          }}
          id="customized-dialog-title"
        >
          <Typography
            color="text.primary"
            variant="h5"
            justifyContent="flex-start"
            textAlign="left"
            mt="-8px"
            fontWeight={700}
          >
            ماموریت {missionNo}
          </Typography>

          <IconButton
            aria-label="close"
            onClick={() => setOpenDetail(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 1,
              p: 2,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            pt: '0px',
            pb: 1.5,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',

            '& .MuiTypography-subtitle1': {
              fontSize: '18px',
            },

            '& .MuiTypography-subtitle2': {
              fontSize: '16px',
            },

            '& .MuiButton-root': {
              fontSize: '16px',
            },
          }}
        >
          <Card
            sx={{
              boxShadow: 'none',
              border: 0,
            }}
          >
            <CardContent>
              <Stack width="100%">
                <Typography
                  color="text.primary"
                  variant="subtitle1"
                  justifyContent="flex-start"
                  textAlign="left"
                  fontWeight="bold"
                >
                  فرستنده
                </Typography>

                <Typography
                  color="text.primary"
                  variant="subtitle2"
                  justifyContent="flex-start"
                  textAlign="left"
                  width="100%"
                >
                  {parcelData?.[0]?.from?.location?.address
                    ? `${parcelData[0].from.location.address} - ${
                        parcelData[0].from.contact?.first_name ?? ''
                      } ${parcelData[0].from.contact?.last_name ?? ''}`
                    : '-'}
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          <TaskBarcodeScan
            detailList={parcelData}
            onScan={(result) => {
              if (result?.matched && result?.matchedItem) {
                handleBarcode(result.matchedItem.barcode);
              }
            }}
          />

          <Box
            ref={parcelScrollerRef}
            data-testid="parcel-scroll-container"
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch',
              scrollbarGutter: 'stable',
              overflowAnchor: 'auto',
              pr: 0.25,
              pb: 1,
            }}
          >
          {[
            {
              id: 'scanned',
              title: 'مرسولات اسکن‌شده',
              rows: scannedParcels,
              empty: 'هنوز مرسوله‌ای اسکن نشده است',
            },

            {
              id: 'unscanned',
              title: 'مرسولات اسکن‌نشده',
              rows: unscannedParcels,
              empty: 'همه مرسوله‌ها اسکن شدند',
            },
          ].map((section) => (
            <Box
              key={section.id}
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: '16px',
                border: '1px solid',

                borderColor:
                  section.id === 'scanned' ? 'success.main' : 'divider',

                bgcolor:
                  section.id === 'scanned'
                    ? 'rgba(18, 160, 0, 0.08)'
                    : 'background.paper',
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mb={1}
                sx={{
                  position: 'sticky',
                  top: -12,
                  zIndex: 2,
                  mx: -1.5,
                  mt: -1.5,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: '16px 16px 0 0',
                  bgcolor:
                    section.id === 'scanned'
                      ? 'rgba(232, 247, 230, 0.98)'
                      : 'background.paper',
                }}
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  {section.id === 'scanned' ? (
                    <CheckCircleIcon
                      sx={{
                        color: 'success.main',
                      }}
                    />
                  ) : (
                    <RemoveCircleOutlineIcon
                      sx={{
                        color: 'error.main',
                      }}
                    />
                  )}

                  <Typography fontWeight={800}>{section.title}</Typography>
                </Stack>

                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{
                    px: 1.25,
                    py: 0.25,
                    borderRadius: '999px',

                    bgcolor:
                      section.id === 'scanned'
                        ? 'success.main'
                        : 'background.muted',

                    color:
                      section.id === 'scanned'
                        ? 'common.white'
                        : 'text.primary',
                  }}
                >
                  {section.rows.length}
                </Typography>
              </Stack>

              {section.rows.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    py: 1,
                    pr: 0.5,
                  }}
                >
                  {section.empty}
                </Typography>
              ) : null}

              {section.rows.map(({ item, index, key }) => {
                const scanned = section.id === 'scanned';

                return (
                  <Grid
                    key={key}
                    mt={1}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <Accordion
                      expanded={expanded === index}
                      onChange={() =>
                        setExpanded(expanded === index ? null : index)
                      }
                      slots={{
                        transition: Fade,
                      }}
                      slotProps={{
                        transition: {
                          timeout: 400,
                        },
                      }}
                      sx={{
                        '& .MuiAccordion-region': {
                          height: expanded === index ? 'auto' : 0,
                        },

                        '& .MuiAccordionDetails-root': {
                          display: expanded === index ? 'block' : 'none',

                          padding: 0,
                        },

                        '& .MuiAccordionSummary-root': {
                          padding: 0,
                        },

                        borderRadius: '8px !important',

                        boxShadow: 'unset',

                        bgcolor: scanned
                          ? 'rgba(18, 160, 0, 0.12)'
                          : 'background.card',

                        border: '1px solid',

                        borderColor: scanned ? 'success.main' : 'transparent',

                        padding: '0px 16px',

                        width: '100%',
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                        sx={{
                          flexDirection: 'row-reverse',

                          justifyContent: 'end',
                        }}
                      >
                        <Stack
                          width="100%"
                          justifyContent="end"
                          flexDirection="row"
                          alignItems="center"
                          flexWrap="wrap"
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="end"
                            width="100%"
                            gap={1}
                          >
                            <Typography
                              color="text.primary"
                              variant="subtitle1"
                              fontWeight="bold"
                              textAlign="left"
                            >
                              مرسوله {item?.parcel_no ?? item?.parcelNo ?? '-'}
                            </Typography>

                            {scanned ? (
                              <CheckCircleIcon
                                aria-label="scanned"
                                sx={{
                                  color: 'success.main',
                                  fontSize: 24,
                                  flexShrink: 0,
                                }}
                              />
                            ) : (
                              <RemoveCircleOutlineIcon
                                sx={{
                                  color: 'error.main',
                                  fontSize: 22,
                                  flexShrink: 0,
                                }}
                              />
                            )}
                          </Stack>

                          <Typography
                            color="text.primary"
                            variant="subtitle2"
                            justifyContent="flex-start"
                            textAlign="left"
                            width="100%"
                          >
                            {item?.hasStatus ? item?.stringStatus : ''}
                          </Typography>
                        </Stack>
                      </AccordionSummary>

                      <AccordionDetails>
                        <Stack width="100%">
                          <Typography
                            color="text.primary"
                            variant="subtitle1"
                            justifyContent="flex-start"
                            textAlign="left"
                            fontWeight="bold"
                          >
                            گیرنده
                          </Typography>

                          <Typography
                            color="text.primary"
                            variant="subtitle2"
                            justifyContent="flex-start"
                            textAlign="left"
                            width="100%"
                          >
                            {item?.to?.location?.address
                              ? `${item.to.location.address} - ${
                                  item?.to?.contact?.first_name ?? ''
                                } ${item?.to?.contact?.last_name ?? ''}`
                              : '-'}
                          </Typography>
                        </Stack>
                        <Stack
                          justifyContent="space-between"
                          display="flex"
                          flexDirection={isSmallerThan290 ? 'column' : 'row'}
                          flexWrap="wrap-reverse"
                        >
                          <Stack>
                            <Typography
                              color="text.primary"
                              variant="subtitle1"
                              justifyContent="flex-start"
                              textAlign="left"
                              fontWeight="bold"
                              mt={2}
                            >
                              وزن
                            </Typography>

                            <Typography
                              color="text.primary"
                              variant="subtitle2"
                              justifyContent="flex-start"
                              textAlign="left"
                              width="100%"
                            >
                              {addCommas(
                                item?.parcel_items?.[0]?.weight || '-'
                              )}
                            </Typography>
                          </Stack>

                          <Stack>
                            <Typography
                              color="text.primary"
                              variant="subtitle1"
                              justifyContent="flex-start"
                              textAlign="left"
                              fontWeight="bold"
                              mt={2}
                            >
                              شماره مرسوله
                            </Typography>

                            <Typography
                              color="text.primary"
                              variant="subtitle2"
                              justifyContent="flex-start"
                              textAlign="left"
                              width="100%"
                            >
                              {item?.parcel_no ?? item?.parcelNo ?? '-'}
                            </Typography>
                          </Stack>
                        </Stack>
                        <Stack
                          justifyContent="space-between"
                          display="flex"
                          flexDirection={isSmallerThan290 ? 'column' : 'row'}
                          flexWrap="wrap-reverse"
                        >
                          <Stack>
                            <Typography
                              color="text.primary"
                              variant="subtitle1"
                              justifyContent="flex-start"
                              textAlign="left"
                              fontWeight="bold"
                              mt={2}
                              width="100%"
                            >
                              اندازه مرسوله
                            </Typography>

                            <Typography
                              color="text.primary"
                              variant="subtitle2"
                              justifyContent="flex-start"
                              textAlign="left"
                              width="100%"
                            >
                              {item?.parcel_properties?.length || '-'}*
                              {item?.parcel_properties?.width || '-'}*
                              {item?.parcel_properties?.height || '-'}
                            </Typography>
                          </Stack>

                          <Stack>
                            <Typography
                              color="text.primary"
                              variant="subtitle1"
                              justifyContent="flex-start"
                              textAlign="left"
                              fontWeight="bold"
                              mt={2}
                              width="100%"
                            >
                              مایعات
                            </Typography>

                            <Typography
                              color="text.primary"
                              variant="subtitle2"
                              justifyContent="flex-start"
                              textAlign="left"
                              width="100%"
                            >
                              {item?.parcel_properties?.is_liquid
                                ? 'بله'
                                : 'خیر'}
                            </Typography>
                          </Stack>

                          <Stack>
                            <Typography
                              color="text.primary"
                              variant="subtitle1"
                              justifyContent="flex-start"
                              textAlign="left"
                              fontWeight="bold"
                              mt={2}
                              width="100%"
                            >
                              شکستنی
                            </Typography>

                            <Typography
                              color="text.primary"
                              variant="subtitle2"
                              justifyContent="flex-start"
                              textAlign="left"
                              width="100%"
                            >
                              {item?.parcel_properties?.is_fragile
                                ? 'بله'
                                : 'خیر'}
                            </Typography>
                          </Stack>
                        </Stack>

                        {/* CONTENT */}
                        <Stack width="100%">
                          <Typography
                            color="text.primary"
                            variant="subtitle1"
                            justifyContent="flex-start"
                            textAlign="left"
                            fontWeight="bold"
                            mt={2}
                          >
                            محتویات مرسوله
                          </Typography>

                          <Typography
                            color="text.primary"
                            variant="subtitle2"
                            justifyContent="flex-start"
                            textAlign="left"
                            width="100%"
                            mb={2}
                          >
                            {item?.parcel_items?.[0]?.description || '-'}
                          </Typography>
                        </Stack>
                        <Stack
                          mb={2}
                          width="100%"
                          justifyContent="center"
                          alignItems="center"
                        >
                          {!parcelDetails[index]?.hasStatus && (
                            <Button
                              size="large"
                              variant="contained"
                              sx={{
                                color: 'primary.contrastText',
                                width: '100%',
                              }}
                              fullWidth
                              onClick={() => handleChangeStatusConfirm(index)}
                            >
                              تغییر وضعیت
                            </Button>
                          )}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                );
              })}
            </Box>
          ))}

          {hasMoreDetails && (
            <Box
              ref={loadMoreSentinelRef}
              aria-hidden="true"
              sx={{ width: '100%', height: 1 }}
            />
          )}

          {loadingMoreDetails && (
            <Stack alignItems="center" justifyContent="center" py={2}>
              <CircularProgress size={26} aria-label="در حال دریافت مرسوله‌ها" />
            </Stack>
          )}
          </Box>

          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            sx={{
              flexShrink: 0,
              pt: 1.5,
              bgcolor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Button
              variant="text"
              disableRipple
              sx={{
                width: '100%',
                mb: showBulkChangeStatus ? 1 : 0,
                color: 'text.primary',
                fontWeight: 800,
                pointerEvents: 'none',
              }}
            >
              کل مرسوله‌ها: {detailTotalCount}
            </Button>
            {showBulkChangeStatus && (
              <React.Fragment>
              {[
                { rows: scannedParcels, label: 'تغییر وضعیت اسکن‌شده‌ها' },
                { rows: unscannedParcels, label: 'تغییر وضعیت اسکن‌نشده‌ها' },
              ].map(({ rows, label }, index) => (
                <Button
                  key={label}
                  size="large"
                  variant={index === 0 ? 'contained' : 'outlined'}
                  disabled={getPendingActivities(rows).length === 0}
                  sx={{
                    color: index === 0 ? 'primary.contrastText' : 'primary.main',
                    width: '100%',
                    mb: 1.5,
                  }}
                  fullWidth
                  onClick={() => handleGroupChangeStatus(rows, label)}
                >
                  {label} ({getPendingActivities(rows).length})
                </Button>
              ))}
              </React.Fragment>
            )}
          </Grid>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

export default DetailModal;
